import * as R from 'ramda'

import { MessageTransport, Type } from '../constants/communications'
import { Business, Contact, Nil, Patient, User } from '../types'
import { formatPhoneNumber } from './phoneUtils'

const getBusinessToEntry = (business: Business, phoneSelect: boolean) =>
  phoneSelect ? formatPhoneNumber(business.communicationsPhone) : business.email

const getContactToEntry = (contact: Contact, phoneSelect: boolean) =>
  phoneSelect ? formatPhoneNumber(contact.phone) : contact.email

const getClientToEntry = (client: User, phoneSelect: boolean) =>
  phoneSelect ? formatPhoneNumber(client.mobilePhone) : client.email || ''

const toFieldContainsToEntry = (toField: string, toEntry: string) =>
  !R.isEmpty(toEntry) && toField.includes(toEntry)

type Entities = {
  businesses?: Business[]
  client?: User
  contacts?: Contact[]
  patient?: Patient
}

export const formRecipients = (
  entities: Entities,
  toEntriesString?: string | Nil,
  transportProp?: MessageTransport | string | Nil,
) => {
  const { client, patient, contacts = [], businesses = [] } = entities
  const basicRecipientPayload = { clientId: client?.id, patientId: patient?.id }
  const transport = transportProp || MessageTransport.EMAIL

  if (transport === MessageTransport.LOG_PHONE_CALL) {
    const contactId = contacts[0]?.id
    const businessId = businesses[0]?.id

    const recipientType = contactId
      ? Type.Contact
      : businessId
        ? Type.Business
        : Type.Client

    return [{ ...basicRecipientPayload, contactId, businessId, recipientType }]
  }

  if (transport === MessageTransport.BOOP) {
    return [{ ...basicRecipientPayload, recipientType: Type.Client }]
  }

  if (!toEntriesString) return []

  const phoneSelect = transport === MessageTransport.SMS
  const clientToEntry = client
    ? getClientToEntry(client, phoneSelect)
    : undefined
  const contactToEntries = contacts
    .map(contact => ({
      toEntry: getContactToEntry(contact, phoneSelect)?.trim(),
      id: contact.id,
    }))
    .filter(R.prop('toEntry'))
  const businessToEntries = businesses
    .map(business => ({
      toEntry: getBusinessToEntry(business, phoneSelect)?.trim(),
      id: business.id,
    }))
    .filter(R.prop('toEntry'))

  const toEntries = toEntriesString
    .split(',')
    .map(toEntry => toEntry.trim())
    .filter(Boolean)

  const clientRecipients =
    clientToEntry && toFieldContainsToEntry(toEntriesString, clientToEntry)
      ? [{ ...basicRecipientPayload, recipientType: Type.Client }]
      : []

  const contactRecipients = contactToEntries
    .filter(({ toEntry }) => toFieldContainsToEntry(toEntriesString, toEntry))
    .map(({ id }) => ({
      ...basicRecipientPayload,
      contactId: id,
      recipientType: Type.Contact,
    }))

  const businessRecipients = businessToEntries
    .filter(({ toEntry }) => toFieldContainsToEntry(toEntriesString, toEntry))
    .map(({ id }) => ({
      ...basicRecipientPayload,
      businessId: id,
      recipientType: Type.Business,
    }))

  const notDirectCommunicationToEntries = R.uniq([
    ...contactToEntries.map(R.prop('toEntry')),
    ...businessToEntries.map(R.prop('toEntry')),
    clientToEntry,
  ])
  const directCommunicationRecipients = R.difference(
    toEntries,
    notDirectCommunicationToEntries,
  ).map(toEntry => ({
    ...basicRecipientPayload,
    [phoneSelect ? 'phoneNumber' : 'email']: toEntry,
    recipientType: Type.Direct,
  }))

  return [
    ...clientRecipients,
    ...contactRecipients,
    ...businessRecipients,
    ...directCommunicationRecipients,
  ]
}
